import { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
    SectionForm,
    Divider,
    Link,
    Spacer,
    Text,
    Icon,
    Tooltip,
    Markdown,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { ModalOptions } from '@Sage/ssm-web5';
import SelectHoi from 'components/SelectHoi';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { FuzzyMap } from 'utils/fuzzy';
import {
    ACTION_OPERATORS,
    ACTION_OPERATORS_DEFAULTS,
    ACTION_OPERATORS_NO_INPUT,
} from '../../../../../../utils';

import DateTime from 'containers/components/Fields/DateTime';
import InputTemplated from '../../InputTemplated';
import InputNDatesOperatorForm from './InputNDatesOperatorForm';
import InputWeekDayOperatorForm from './InputWeekDayOperatorForm';
import AdvancedSelect from 'components/AdvancedSelect';
import Select from 'containers/components/Fields/Select';
import SelectFuzzy from 'containers/components/Fields/SelectFuzzy';
import MultiSelect from 'containers/components/Fields/MultiSelect';
import MultiSelectFuzzy from 'containers/components/Fields/MultiSelectFuzzy';
import AbsoluteEmpty from 'components/AbsoluteEmpty';
import CustomizationIllustration from 'components/illustrations/CustomizationIllustration';

import './styles.scss';

const UpdateEntityRelatedAction = memo(({ onChange, form, action, variables }) => {
    const theme = useTheme();
    const [fields, setFields] = useState([]);
    const [operators, setOperators] = useState({});
    const [modalFieldsOpen, setModalFieldsOpen] = useState(false);
    const [selectedFields, setSelectedFields] = useState({});
    const formEntity = useRef();
    const inputTemplatedProps = useRef({});
    const disabledFields = useRef({});
    const boolOptions = useMemo(
        () => [
            {
                label: getLiteral('cfm_label_yes'),
                value: true,
            },
            {
                label: getLiteral('cfm_label_no'),
                value: false,
            },
        ],
        [],
    );

    useEffect(() => {
        if (!variables) return;
        let fields = variables?.values?.fields[form.parameters?.entity?.entity];
        setFields(
            fields?.map((item) => {
                disabledFields.current[item.name] = { options: [] };
                return {
                    label: item.name,
                    id: item.name,
                    options:
                        item.fields?.map?.((field) => {
                            const checkMandatories =
                                form.name === 'create_entity' && field.mandatory;
                            if (checkMandatories) {
                                disabledFields.current[item.name].options.push(field.key);
                            }
                            return {
                                ...field,
                                label: getLiteral(field.literal) + (checkMandatories ? ' *' : ''),
                                id: field.key,
                            };
                        }) || [],
                };
            }) || [],
        );

        if (form.fields?.length > 0) {
            const newSelectedFields = {};
            const fieldsBySection = {};
            form.fields.forEach((field) => {
                const fieldKey = field.key;
                fields?.forEach((section) => {
                    const foundField = section.fields?.find((f) => f.key === fieldKey);
                    if (foundField) {
                        if (!fieldsBySection[section.name]) {
                            fieldsBySection[section.name] = [];
                        }
                        fieldsBySection[section.name].push(fieldKey);
                    }
                });
            });

            Object.keys(fieldsBySection).forEach((sectionName) => {
                const sectionId = fields?.find((section) => section.name === sectionName)?.name;
                if (sectionId) {
                    newSelectedFields[sectionId] = {
                        isSelected: false,
                        options: fieldsBySection[sectionName],
                    };
                }
            });

            setSelectedFields(newSelectedFields);
        }

        setOperators(variables?.operators);

        let options = [];
        variables?.values?.variables?.entity?.sections?.forEach((section) => {
            options.push({
                label: section.name,
                key: variables?.values?.variables?.entity?.value,
                options: section.fields.map((field) => ({
                    label: getLiteral(field.literal),
                    value: field.value,
                    type: field.type,
                    table: field.table,
                })),
            });
        });

        const filterOptions = variables?.values?.variables?.related?.reduce(
            (acc, item) => {
                acc.push({
                    label: getLiteral(item.description),
                    value: item.value,
                    iconType: item.entity,
                });

                item.sections?.forEach((section) => {
                    options.push({
                        label: section.name,
                        key: item.value,
                        options: section.fields.map((field) => ({
                            label: getLiteral(field.literal),
                            value: field.value,
                            type: field.type,
                            table: field.table,
                        })),
                    });
                });
                return acc;
            },
            [
                {
                    label: getLiteral(variables?.values?.variables?.entity?.description),
                    value: variables?.values?.variables?.entity?.value,
                    iconType: variables?.values?.variables?.entity?.entity,
                },
            ],
        );

        inputTemplatedProps.current = {
            filterOptions,
            options,
        };
    }, [variables, form]);

    useEffect(() => {
        if (
            (form?.parameters?.entity !== formEntity.current || !formEntity.current) &&
            fields?.length > 0
        ) {
            formEntity.current = form?.parameters?.entity;
            if (form.name === 'create_entity') {
                // Adding default mandatory fields
                if (variables?.values?.fields) {
                    const mandatoryFields = {};
                    const entityFields =
                        variables?.values?.fields[form.parameters?.entity?.entity] || [];

                    entityFields.forEach((section) => {
                        const mandatoryOptions =
                            section.fields
                                ?.filter((field) => field.mandatory)
                                .map((field) => field.key) || [];

                        if (mandatoryOptions.length > 0) {
                            mandatoryFields[section.name] = {
                                isSelected: false,
                                options: mandatoryOptions,
                            };
                        }
                    });

                    if (Object.keys(mandatoryFields).length > 0) {
                        setTimeout(() => onSelectFields(mandatoryFields));
                    }
                }
            } else {
                setSelectedFields({});
            }
        }
    }, [
        fields?.length,
        form,
        form?.name,
        form?.parameters?.entity,
        onSelectFields,
        variables?.values?.fields,
    ]);

    const changeValues = useCallback(
        (field) => (value) => {
            onChange((current) => {
                return {
                    ...current,
                    values: {
                        ...(current?.values || {}),
                        [field]: value,
                    },
                };
            });
        },
        [onChange],
    );

    const changeFormat = useCallback(
        (formatField, field) => (value) => {
            onChange((current) => {
                return {
                    ...current,
                    format: {
                        ...(current?.format || {}),
                        [field]: { ...(current?.format?.[field] || {}), [formatField]: value },
                    },
                };
            });
        },
        [onChange],
    );

    const changeOperator = useCallback(
        (field) => (value) => {
            onChange((current) => {
                return {
                    ...current,
                    operators: { ...current.operators, [field]: value },
                    values: {
                        ...(current?.values || {}),
                        [field]: undefined,
                    },
                };
            });
        },
        [onChange],
    );

    const getCalculatedSelectFields = useCallback((field) => {
        const id = field.value.split('.')?.[1];
        const filteredOptions = inputTemplatedProps.current?.options
            ?.map((section) => ({
                ...section,
                options: section.options?.filter(
                    (option) =>
                        option.type === field.type &&
                        option.table?.table === field.table?.table &&
                        !option.value.includes(id),
                ),
            }))
            .filter((section) => section.options?.length > 0);

        const filteredFilterOptions = inputTemplatedProps.current?.filterOptions?.filter((filter) =>
            filteredOptions?.some((section) => section.key === filter.value),
        );

        return {
            options: filteredOptions || [],
            filterOptions: filteredFilterOptions || [],
        };
    }, []);

    const getSelectFieldsByTypes = useCallback((types) => {
        const filteredOptions = inputTemplatedProps.current?.options
            ?.map((section) => ({
                ...section,
                options: section.options?.filter((option) => !types || types.includes(option.type)),
            }))
            .filter((section) => section.options?.length > 0);

        const filteredFilterOptions = inputTemplatedProps.current?.filterOptions?.filter((filter) =>
            filteredOptions?.some((section) => section.key === filter.value),
        );

        return {
            options: filteredOptions || [],
            filterOptions: filteredFilterOptions || [],
        };
    }, []);

    const getFieldByType = useCallback(
        (field) => {
            let list, listProps, Component;
            let calculatorOptions, calculatedOptions;
            switch (form?.operators?.[field.value]) {
                case 'calculated_string':
                    calculatorOptions = getSelectFieldsByTypes();
                    return (
                        <InputTemplated
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            placeholder={getLiteral(
                                'label_automation_calculated_value_text_placeholder',
                            )}
                            isFullWidth
                            insertFieldOptions={calculatorOptions}
                            locale={form?.format?.[field.value]?.locale}
                            timeZone={form?.format?.[field.value]?.timeZone}
                            onChangeLocale={changeFormat('locale', field.value)}
                            onChangeTimeZone={changeFormat('timeZone', field.value)}
                        />
                    );
                case 'calculated_calculator_int':
                case 'calculated_calculator_float':
                    calculatorOptions = getSelectFieldsByTypes([
                        'currency',
                        'decimal',
                        'int',
                        'percentage',
                    ]);

                    // Checking valid characters
                    const error =
                        form?.values?.[field.value] &&
                        !form?.values?.[field.value]?.match(
                            /^(?:[0-9+\-/*()%#\s._]|{{[a-z0-9._\s]+}})*$/gi,
                        )
                            ? getLiteral('error_automation_calculated_value_number')
                            : undefined;

                    return (
                        <InputTemplated
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            placeholder={getLiteral(
                                'label_automation_calculated_value_number_placeholder',
                            )}
                            error={error}
                            infoPopover={
                                <div className="fm-automation__update-entity-related__info-popover">
                                    <Text type="subtitle" medium>
                                        {getLiteral('label_formula_rules')}
                                    </Text>
                                    <Spacer y={3} />
                                    <Text>{getLiteral('label_formula_rules_desc')}</Text>
                                    <Spacer y={1} />
                                    <Text>
                                        {getLiteralWithParameters(
                                            'label_formula_rules_desc_example',
                                            ['{{ .event.data.total }}', '{{ .event.data.tax }}'],
                                        )}
                                    </Text>
                                    <Spacer y={1} />
                                    <Markdown
                                        content={getLiteral('label_formula_rules_desc_2')}
                                        overrides={{ a: { target: '_blank' } }}
                                    />
                                </div>
                            }
                            isFullWidth
                            insertFieldOptions={calculatorOptions}
                        />
                    );
                case 'calculated_datetime':
                case 'calculated_boolean':
                case 'calculated_single_list':
                case 'calculated_multi_list':
                    calculatedOptions = getCalculatedSelectFields(field);
                    return (
                        <AdvancedSelect
                            searchPlaceholder={getLiteral('placeholder_search_field')}
                            menuTitle={getLiteral('cfm_action_add_field')}
                            placeholder={getLiteral('label_placeholder_field')}
                            onChange={changeValues(field.value)}
                            options={calculatedOptions.options}
                            defaultFilter={calculatedOptions.filterOptions?.[0]?.value}
                            filterOptions={calculatedOptions.filterOptions}
                            value={form?.values?.[field.value]}
                            isFullWidth
                            useMenuPortal={false}
                        />
                    );
                case 'specific_boolean':
                    return (
                        <SelectHoi
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            options={boolOptions}
                            usePlainValue
                            isFullWidth
                            useMenuPortal={false}
                        />
                    );

                case 'specific_datetime':
                    return (
                        <DateTime
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                        />
                    );
                case 'days_after_datetime':
                case 'week_after_datetime':
                case 'month_after_datetime':
                    calculatedOptions = getCalculatedSelectFields(field);
                    return (
                        <InputNDatesOperatorForm
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            options={calculatedOptions.options}
                            defaultFilter={calculatedOptions.filterOptions?.[0]?.value}
                            filterOptions={calculatedOptions.filterOptions}
                        />
                    );
                case 'next_day_week_datetime':
                    calculatedOptions = getCalculatedSelectFields(field);
                    return (
                        <InputWeekDayOperatorForm
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            options={calculatedOptions.options}
                            defaultFilter={calculatedOptions.filterOptions?.[0]?.value}
                            filterOptions={calculatedOptions.filterOptions}
                        />
                    );
                case 'specific_single_list':
                    list = field.table?.table;
                    listProps = FuzzyMap[list?.toLowerCase()] || list || {};
                    Component = listProps?.list ? SelectFuzzy : Select;
                    return (
                        <Component
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            cacheOptions={false}
                            list={field?.table?.table}
                            {...listProps}
                            useMenuPortal={false}
                            usePlainValue
                        />
                    );
                case 'specific_multi_list':
                    list = field.table?.table;
                    listProps = FuzzyMap[list?.toLowerCase()] || list || {};
                    Component = listProps?.list ? MultiSelectFuzzy : MultiSelect;
                    return (
                        <Component
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            list={field?.table?.table}
                            {...listProps}
                            useMenuPortal={false}
                            usePlainValue
                        />
                    );
                default:
                    return null;
            }
        },
        [
            boolOptions,
            changeFormat,
            changeValues,
            form?.format,
            form?.operators,
            form?.values,
            getCalculatedSelectFields,
            getSelectFieldsByTypes,
        ],
    );

    const showFieldsModal = useCallback(() => {
        setModalFieldsOpen(true);
    }, []);

    const handleOnClose = useCallback(() => {
        setModalFieldsOpen(false);
    }, []);

    const onChangeSelectedFields = useCallback((selected) => {
        setSelectedFields(selected);
    }, []);

    const onSelectFields = useCallback(
        (selected) => {
            if (!fields) return;
            const fieldsMap = fields.reduce((obj, section) => {
                section.options.forEach((option) => (obj[option.key] = option));
                return obj;
            }, {});

            const fieldsList = Object.entries(selected).reduce((obj, [section, value]) => {
                value.options.forEach((option) => {
                    const field = fieldsMap[option];
                    obj.push({
                        ...field,
                        section,
                        label: getLiteral(field?.literal),
                        value: field?.key,
                    });
                });
                return obj;
            }, []);

            onChange((current) => {
                return {
                    ...current,
                    fields: fieldsList,
                    operators: {
                        ...current.operators,
                        ...fieldsList?.reduce((obj, value) => {
                            if (current.operators?.[value?.value]) return obj;
                            else {
                                obj[value?.value] = ACTION_OPERATORS_DEFAULTS[value?.type];
                            }
                            return obj;
                        }, {}),
                    },
                };
            });

            setModalFieldsOpen(false);

            setSelectedFields(selected);
        },
        [fields, onChange],
    );

    const fieldsForm = useMemo(() => {
        const groupedFields = form?.fields?.reduce((acc, field) => {
            if (!acc[field.section]) {
                acc[field.section] = [];
            }
            acc[field.section].push(field);
            return acc;
        }, {});

        return Object.entries(groupedFields || {}).map(([section, fields]) => (
            <SectionForm key={section} title={section}>
                <div className="fm-automation__update-entity-related__fields">
                    {fields.map((field) => {
                        const operatorOptions = operators?.[field.type]?.map((operator) => ({
                            label: getLiteral(ACTION_OPERATORS[operator]),
                            value: operator,
                        }));

                        const hasInput = !ACTION_OPERATORS_NO_INPUT.includes(
                            form?.operators?.[field.value],
                        );

                        const isMandatory = form.name === 'create_entity' && field.mandatory;

                        return (
                            <div
                                key={field.key}
                                className="fm-automation__update-entity-related__fields-form"
                            >
                                <div className="fm-automation__update-entity-related__fields-form__title">
                                    <Text>
                                        {getLiteral(field.literal)} {isMandatory ? '*' : null}
                                    </Text>
                                    {!isMandatory && (
                                        <Tooltip
                                            content={getLiteral('action_delete_filed')}
                                            placement="topRight"
                                        >
                                            <span>
                                                <Icon
                                                    name="less"
                                                    size="large"
                                                    color={theme.colors.actionMinor[500]}
                                                    onClick={() => {
                                                        onChange((current) => {
                                                            const fieldsList =
                                                                current?.fields?.filter(
                                                                    (f) => f.value !== field.value,
                                                                );

                                                            const updatedSelectedFields = {
                                                                ...selectedFields,
                                                            };
                                                            Object.keys(
                                                                updatedSelectedFields,
                                                            ).forEach((sectionKey) => {
                                                                const section =
                                                                    updatedSelectedFields[
                                                                        sectionKey
                                                                    ];
                                                                const newOptions =
                                                                    section.options.filter(
                                                                        (option) =>
                                                                            option !== field.key,
                                                                    );

                                                                if (newOptions.length === 0) {
                                                                    delete updatedSelectedFields[
                                                                        sectionKey
                                                                    ];
                                                                } else {
                                                                    updatedSelectedFields[
                                                                        sectionKey
                                                                    ] = {
                                                                        ...section,
                                                                        options: newOptions,
                                                                    };
                                                                }
                                                            });
                                                            setSelectedFields(
                                                                updatedSelectedFields,
                                                            );

                                                            return {
                                                                ...current,
                                                                fields: fieldsList,
                                                                operators: {
                                                                    ...current.operators,
                                                                    ...fieldsList?.reduce(
                                                                        (obj, value) => {
                                                                            if (
                                                                                current.operators?.[
                                                                                    value?.value
                                                                                ]
                                                                            )
                                                                                return obj;
                                                                        },
                                                                        {},
                                                                    ),
                                                                },
                                                            };
                                                        });
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                                <SelectHoi
                                    options={operatorOptions}
                                    value={form?.operators?.[field.value]}
                                    onChange={changeOperator(field.value)}
                                    useMenuPortal={false}
                                    isFullWidth
                                    usePlainValue
                                />
                                {hasInput && getFieldByType(field)}
                            </div>
                        );
                    })}
                </div>
            </SectionForm>
        ));
    }, [
        form?.fields,
        form?.operators,
        form?.name,
        operators,
        theme.colors.actionMinor,
        changeOperator,
        getFieldByType,
        onChange,
        selectedFields,
    ]);

    const sectionTitle = useMemo(() => {
        if (!form?.parameters?.entity) return;
        return `${action?.label} ${form?.parameters?.entity?.label}`;
    }, [action?.label, form]);

    const modalOptions = useMemo(() => {
        return {
            overlayClassName: 'fm-automation__update-entity-related-action__modal-overlay',
            title: getLiteral('cfm_action_add_field'),
            schema: fields,
            disabled: disabledFields.current,
            selected: selectedFields,
            isOpen: modalFieldsOpen,
            onChangeSelected: onChangeSelectedFields,
            placeholderSearch: getLiteral('label_search_field'),
            confirmText: getLiteral('action_save'),
            onConfirm: onSelectFields,
            cancelText: getLiteral('action_cancel'),
            onCancel: handleOnClose,
        };
    }, [
        fields,
        handleOnClose,
        modalFieldsOpen,
        onChangeSelectedFields,
        onSelectFields,
        selectedFields,
    ]);

    const showFields = form?.fields?.length > 0;

    return (
        <>
            {form?.parameters?.entity && (
                <SectionForm title={sectionTitle} isExpandable={false}>
                    <div className="fm-automation__update-entity-related-action">
                        {!showFields && (
                            <AbsoluteEmpty
                                svg={<CustomizationIllustration />}
                                title={getLiteral('label_automations_action_update_select_field')}
                                subtitle={getLiteral(
                                    'label_automations_action_update_select_field_desc',
                                )}
                                size="small"
                            />
                        )}
                        {fieldsForm}
                        <Divider />
                        <div className="fm-automation__update-entity-related-action__footer">
                            <Link variation="primary" onClick={showFieldsModal} bold>
                                + {getLiteral('cfm_action_add_field')}
                            </Link>
                        </div>
                    </div>
                    <ModalOptions {...modalOptions} />
                </SectionForm>
            )}
        </>
    );
});

UpdateEntityRelatedAction.getInitialData = (node) => {
    return {
        ...node,
        name: node?.parameters?.entity ? node.name : undefined,
        parameters: {
            ...node.parameters,
            entity: node?.parameters?.entity
                ? {
                      label: getLiteral(node.parameters.entity?.literal),
                      literal: node.parameters.entity?.literal,
                      value: node.parameters.entity?.value,
                      iconType: node.parameters.entity?.entity,
                      entity: node.parameters.entity?.entity,
                  }
                : undefined,
        },
        fields: node.parameters?.values?.map((value) => ({
            ...value?.field,
            label: getLiteral(value?.field?.literal),
            value: value?.field?.key,
        })),
        operators: node.parameters?.values?.reduce((obj, value) => {
            obj[value.field?.key] = value?.value?.type;
            return obj;
        }, {}),
        values: node.parameters?.values?.reduce((obj, value) => {
            obj[value.field?.key] = value?.value?.value;
            return obj;
        }, {}),
        format: node.parameters?.values?.reduce((obj, value) => {
            obj[value.field?.key] = {
                timeZone: value?.format?.timeZone,
                locale: value?.format?.locale,
            };
            return obj;
        }, {}),
    };
};

UpdateEntityRelatedAction.hasEmptyFields = (form) => {
    if (!form.fields?.length) return true;
    return form.fields.some((field) => {
        const operatorExcluded = ACTION_OPERATORS_NO_INPUT.includes(form.operators[field.value]);
        const valueIsUndefined = !form.values[field.value];
        return !operatorExcluded && valueIsUndefined;
    });
};

UpdateEntityRelatedAction.getSaveData = (form) => {
    let values = [];
    if (form.fields?.length > 0) {
        values = form.fields.map((field) => ({
            field,
            value: {
                type: form.operators[field.value],
                value: form.values[field.value],
            },
            format: {
                timeZone: form.format?.[field.value]?.timeZone,
                locale: form.format?.[field.value]?.locale,
            },
        }));
    }

    return { entity: form.parameters.entity, values };
};

export default UpdateEntityRelatedAction;
